import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, NgZone, OnInit, runInInjectionContext, viewChild, ViewEncapsulation } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ProjectAssetMappingService } from '@app/cem/project/content/project-asset-mapping/project-asset-mapping.service';
import { ProjectScreenBehaviorService } from '@app/cem/project/services/project-screenbehavior.service';
import { SelectedTabService } from '@app/cem/project/services/selected-tab.service';
import { AppStorage } from '@app/common/services/data-api/app-storage.service';
import { HandleDates } from '@app/common/services/handle-dates.service';
import { PlexEnvironment } from '@app/env';
import { TabStripScrollingDirective } from '@app/shared/directives/kendohelpers/tabstrip-scrolling.directive';
import { PxdGridMenuComponent } from '@app/shared/kendoui/grid/components/pxd-grid-menu/pxd-grid-menu.component';
import { PxdColumns } from '@app/shared/kendoui/grid/interfaces/pxd-columns';
import { skipProjectColumn } from '@app/shared/library/constant';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ProjectsView } from '@app/shared/models/projects-view';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { RowArgs } from '@progress/kendo-angular-grid';
import { SelectEvent, TabStripComponent, TabStripModule } from '@progress/kendo-angular-layout';
import { ContextMenuComponent, ContextMenuSelectEvent, MenuItemComponent } from '@progress/kendo-angular-menu';
import { LookupPlusService } from 'app/cem/project/services/lookups/lookup-plus.service';
import { ProjectAssetMappingCostCenterService } from 'app/cem/project/services/project-asset-mapping-cost-center.service';
import { ErrorService } from 'app/common/services/error.service';
import { MajorMilestoneProjectTypeVisibilityScheduleView } from 'app/shared/models';
import { ProjectTab } from 'app/shared/models/project/Project-Tab';
import { IProjectTab } from '../../../../shared/interfaces/iproject-tab';
import { Project } from '../../../../shared/models/project';
import { ProjectBaseScenarioComponent } from '../../content/project-base-scenario/project-base-scenario.component';
import { ProjectGridViewService } from '../../services/project-grid-view.service';
import { ProjectLandingDataService } from '../../services/project-landing-data.service';
import { ProjectTabService } from '../../services/project-tab.service';
import { columns } from '../project-view-list/columns';
import { ProjectViewListComponent } from '../project-view-list/project-view-list.component';
import { LocalStorageService } from './../../../../auth/local-storage.service';

interface ProjectTabMenuItemData {
  tabItem: ProjectTab;
  tabIndex: number;
}
@Component({
  selector: 'app-layout',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    ContextMenuComponent,
    MatToolbar,
    MenuItemComponent,
    ProjectBaseScenarioComponent,
    ProjectViewListComponent,
    PxdGridMenuComponent,
    TabStripModule,
    TabStripScrollingDirective,
  ],
})
export class LayoutComponent implements OnInit, AfterViewInit {
  public projectTabService = inject(ProjectTabService);
  public projectLandingDataService = inject(ProjectLandingDataService);
  private lookupPlusService = inject(LookupPlusService);
  private errorService = inject(ErrorService);
  public httpClient = inject(HttpClient);
  public handleDates = inject(HandleDates);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private screenBehaviors = inject(ProjectScreenBehaviorService);
  private localStorage = inject(LocalStorageService);
  private projectGridViewService = inject(ProjectGridViewService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(AppStorage);
  private injector = inject(Injector);
  public environment = inject(PlexEnvironment);

  public icons = kendoIcons;

  public readonly tabstrip = viewChild<TabStripComponent>('tabstrip');

  public newProjectId: string;
  public selected: number = 1;
  public projectRecord: Project;
  public showNewProjectTab: boolean = false;
  public showCreateNewButton: boolean;
  public newProjectTab = {
    ProjectId: -1,
    Name: 'NewProject',
    ProjectTypeId: 0,
    ProjectType: 0,
    IsSelected: true,
    Disabled: false,
  };

  public projectName: string;
  public tabItems = new Array<ProjectTab>();
  public loadingmajormilestonevisibilities: boolean = true;
  public majormilestonevisibilities: MajorMilestoneProjectTypeVisibilityScheduleView[] = [];

  public tabIndex: Array<boolean> = this.createTabArray();
  public loader: boolean = false;
  public selectedProjects: RowArgs[] = [];
  public selectedTabIndex: number;
  public currentAssetTeamId: number;
  public columns: PxdColumns[] = columns;
  public columnSettingsKey: string = 'projectHomeColumns';
  public columnAFEApprovalTeamTitle: string = 'WBS Approval Team';

  constructor() {
    this.projectTabService.projectTab$.subscribe(
      (args) => {
        let tab: ProjectTab = args.ProjectTab;

        let z = this.tabItems.findIndex(x => x.ProjectId === tab.ProjectId);
        let index: number = 0;
        if (z === -1) {
          // TODO: Investigate whether these need to be unique instances, or if they can be injected with the other services above.
          let projectAssetMappingCostCenterService: ProjectAssetMappingCostCenterService = runInInjectionContext(this.injector, () => new ProjectAssetMappingCostCenterService());
          let projectAssetMappingService: ProjectAssetMappingService = runInInjectionContext(this.injector, () => new ProjectAssetMappingService());
          let selectedTabService: SelectedTabService = new SelectedTabService();

          tab.ProjectAssetMappingCostCenterService = projectAssetMappingCostCenterService;
          tab.ProjectAssetMappingService = projectAssetMappingService;
          tab.SelectedTabService = selectedTabService;
          index = this.tabItems.length;

          this.tabItems.push(tab);

          this.projectTabService.projectTabs(this.tabItems);

          if (args.Activate) {
            this.selectTabAsync(this.tabItems.length, tab.ProjectId); //normally you subtract 1 but there's a built-in home tab
          }
        } else {
          this.selectTabAsync(z + 1, null); //plus 1 to account for home tab
        }
      }
    );

    this.newProjectId = 'Create Project';
    this.loadingmajormilestonevisibilities = true;
    this.projectLandingDataService.projectData$.subscribe(
      data => {
        this.projectRecord = data;
        this.majormilestonevisibilities = [];
        this.getMajorMilestoneVisibilities(data);
      }
    );
  }

  selectTabAsync(index: number, projectId: number) {
    if (projectId) {
      this.updateURLParams(projectId);
    }
    this.desiredTabIndex = index;
    this.zone.onMicrotaskEmpty.emit(null);
  }

  public updateURLParams(newProjectId: number) {
    let params = this.activatedRoute.snapshot.queryParams;

    let projectId: string = null;

    if (newProjectId === null) {
      projectId = null;
    } else {
      let existing = parseInt(this.getUncasedParameter(params, 'projectId', '0'));

      if (newProjectId < 0) {
        if (existing == -newProjectId) {
          projectId = null;
        } else {
          return;
        }
      }
      else {
        if (existing === newProjectId) {
          return;
        } else {
          projectId = newProjectId.toString();
        }
      }
    }
    const queryParams: Params = { projectId: projectId };
    this.router.navigate([], { relativeTo: this.activatedRoute, queryParams, queryParamsHandling: 'merge' });
  }

  public closeNewTab(e) {
    let createProjectTab = this.tabItems.findIndex(t => t.ProjectId === -1);

    if (createProjectTab >= 0) {
      this.tabItems.splice(createProjectTab, 1);
      this.closeTab(-1, null);
    }
  }

  public getMajorMilestoneVisibilities(data: Project): void {
    let scheduleId = data.ScheduleId;
    if (scheduleId) {
      let filter = `$filter=ScheduleId eq ${scheduleId}`;
      this.lookupPlusService
        .getTypedLookup<MajorMilestoneProjectTypeVisibilityScheduleView>('MajorMilestoneProjectTypeVisibilityScheduleView', filter)
        .then(mm => {
          this.loadingmajormilestonevisibilities = false;
          this.majormilestonevisibilities = mm.data;
        }
        )
        .catch(e => {
          this.loadingmajormilestonevisibilities = false;
          this.errorService.error('Error Getting Major Milestone Top List.', e);
        });
    } else {
      this.loadingmajormilestonevisibilities = false;
    }
  }

  private desiredTabIndex = -1;

  private getUncasedParameter(params: object, paramName: string, defaultIfMissing: string = ''): string | null {
    paramName = paramName.toLowerCase();

    let lowerCaseParams = Object.keys(params).reduce((newParams, key) => {
      newParams[key.toLowerCase()] = params[key];
      return newParams;
    }, {});

    if (lowerCaseParams[paramName] != null) {
      let valueOfParam = lowerCaseParams[paramName];
      return valueOfParam;
    }
    return defaultIfMissing;
  }

  async ngOnInit() {
    this.showCreateNewButton = true;
    await this.screenBehaviors.getBehaviors();

    if (this.localStorage.getItem('tb-selected-wells').value) {
      this.createNewProjectTab(null);
    }

    this.activatedRoute.queryParams.subscribe(async params => {
      let projectId = parseInt(this.getUncasedParameter(params, 'projectid', '0'));
      if (isNaN(projectId) || !projectId) return;
      await this.openProjectDataItem(projectId);
    });

    this.behaviorsReady = true;
    this.initializeTabsWhenReady();

    this.zone.onMicrotaskEmpty.subscribe({
      next: () => {
        let index = this.desiredTabIndex;
        if (index < 0) return;
        this.zone.runOutsideAngular(() => {
          if (index > 0) {
            const tb = this.tabstrip();
            tb.selectTab(this.desiredTabIndex);
            this.tabIndex[this.desiredTabIndex] = true;
            this.desiredTabIndex = -1;
          }
        });
      }
    });
  }

  public async openProjectDataItem(projectId: number, forceLoadIfAlreadyLoaded: boolean = false) {
    if (!forceLoadIfAlreadyLoaded) {
      let existing = this.tabItems.findIndex(z => z.ProjectId === projectId);
      if (existing > -1) return;
    }
    const projectData = await this.projectGridViewService.getProjectData(projectId);
    if (projectData && projectData.value && projectData.value.length > 0) {
      let dataItem = projectData.value[0] as ProjectsView;
      this.projectTabService.open(dataItem, true, false);
    }
  }

  public closeTab(index, e) {
    // New Project Tab is a special tab.
    // It is not part of the dynamic set of tabs created to edit existing projects
    // and therefore does not have an index number aka it is undefined
    // If the tab to be closed has an undefined index then set the showNewProjectTab flag to false
    if (index < 0) {
      this.showNewProjectTab = false;
      this.showCreateNewButton = true;
      this.tabstrip().selectTab(0);
      return;
    }

    this.tabstrip().selectTab(0);

    this.projectTabService.remove(e.ProjectId, this.tabItems);
    this.updateURLParams(-e.ProjectId);
  }

  public tabAction(tab: SelectEvent) {
    this.tabIndex[tab.index] = true;
    this.selectedTabIndex = tab.index;

    let projectId = null;

    if (tab.index > 0) {
      let projectTab = this.tabItems[tab.index - 1];
      projectId = projectTab.ProjectId;
    }

    this.updateURLParams(projectId);
  }

  onSelectedProjectsChanged(selected: RowArgs[]) {
    this.selectedProjects = selected;
  }

  public openProjects(e: any) {
    let lastIndex;
    this.selectedProjects.forEach((row) => {
      this.projectTabService.open(row.dataItem, true, false);

      lastIndex = this.tabItems.findIndex(f => f.ProjectId === row.dataItem.ProjectId);
    });
    this.selectTabAsync(lastIndex + 1, null);
  }

  public createNewProjectTab(e: any) {
    if (e)
      e.preventDefault();
    let v: string = '';
    let w: number = 0;
    let x: string = 'Create Project';
    let y: number = -1;
    let z: IProjectTab = {
      ProjectId: y,
      Name: x,
      ProjectTypeId: w,
      ProjectType: v,
      DefaultProjectTeamId: null,
      FunctionId: null,
      ScenarioId: 0,
      ProjectStatusId: 1,
      IsProjectTeamSync: false,
      AssetTeamId: 0,
      IsSelected: true,
      Disabled: false,
    };
    let zObj: ProjectTab = new ProjectTab(z);
    this.projectTabService.projectTab(zObj, true);
  }

  private viewReady = false;
  private behaviorsReady = false;

  private initializeTabsWhenReady(): void {
    if (!this.viewReady || !this.behaviorsReady) return;

    this.projectTabService.processTabs(this.tabstrip());
    this.displayCurrentBasinProjectTabsOnly();
  }

  public ngAfterViewInit(): void {
    this.viewReady = true;
    this.initializeTabsWhenReady();

    this.store.knownEvents.assetTeamSelected.subscribe(selected => {
      if (!selected) return;

      this.currentAssetTeamId = Number(this.store.knownStore.assetTeam.AssetTeamId);

      this.columns.forEach(column => {
        if (skipProjectColumn.includes(column.title)) {
          column.params.hide = true;
        }
      });
      this.columns.forEach(column => {
        if (column.title === this.columnAFEApprovalTeamTitle) {
          column.params.hide = false;
        }
      });

      this.displayCurrentBasinProjectTabsOnly();
      this.cdr.detectChanges();
    });
  }

  public getLoader(e: any) {
    this.loader = e;
  }

  public selectTabMenuContextItem(event: ContextMenuSelectEvent) {
    switch (event.item.text) {
      case 'Close All':
        this.closeAllTabs();
        break;
      case 'Close Others':
        let data: ProjectTabMenuItemData = event.item.data;
        this.closeOtherTabs(data);
        break;
      default:
        break;
    }
  }

  private closeAllTabs() {
    this.tabstrip().selectTab(0);

    this.projectTabService.removeAll(this.tabItems);
    this.updateURLParams(null);
  }

  private closeOtherTabs(data: ProjectTabMenuItemData) {
    if (data.tabIndex + 1 !== this.selectedTabIndex) {
      this.tabstrip().selectTab(0);
    }

    this.projectTabService.removeAllOthers(data.tabItem.ProjectId, this.tabItems);

    setTimeout(t => {
      this.tabstrip().selectTab(1);
    })
  }

  private displayCurrentBasinProjectTabsOnly() {
    if (this.currentAssetTeamId == null) {
      const assetTeamId = this.store?.knownStore?.assetTeam?.AssetTeamId;
      if (assetTeamId != null) {
        this.currentAssetTeamId = Number(assetTeamId);
      }
    }

    if (this.currentAssetTeamId == null) {
      return;
    }

    if (this.tabstrip() && this.tabItems.length > 0) {
      // Close project tabs tied to another basin (different from the current basin)
      const tabs = this.tabItems.filter(t => t.AssetTeamId !== this.currentAssetTeamId);
      this.closeOtherBasinTabs(tabs);
      // Display only project tabs tied to the current basin
      this.tabItems = this.tabItems.filter(t => t.AssetTeamId === this.currentAssetTeamId);
    }
  }

  private closeOtherBasinTabs(tabs: ProjectTab[]) {
    this.tabstrip().selectTab(0);
    this.projectTabService.removeAll(tabs);
    this.updateURLParams(null);
  }

  /**
   * Cheap way of presetting 100 tab select booleans as false.
   * TODO: Use the observable protectTabs$ to dynamically set this
   *
   * @private
   * @returns
   * @memberof LayoutComponent
   */
  private createTabArray() {
    let array = [];

    for (let x = 0; x < 100; x++) {

      if (x === 0) {
        array.push(true);
      } else {
        array.push(false);
      }
    }
    return array;
  }
}